import React, { useState } from 'react';

const RootAccess = ({ onRootAccess }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [accessState, setAccessState] = useState('idle'); // idle, verifying, escalating, granted

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password === 'sudo_master_admin') {
      setAccessState('verifying');
      setError('');
      
      // Sequence: Verifying -> Escalating -> Granted
      setTimeout(() => {
        setAccessState('escalating');
        setTimeout(() => {
          setSuccess(true);
          setAccessState('granted');
          onRootAccess();
        }, 1500);
      }, 1000);
    } else {
      setError('ACCESS DENIED: INVALID PASSWORD');
      setPassword('');
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
    }
  };

  if (success) {
    return (
      <div style={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        color: '#10b981',
        textAlign: 'center',
        padding: '20px',
        background: 'linear-gradient(180deg, rgba(16, 185, 129, 0.1) 0%, rgba(0,0,0,0) 100%)'
      }}>
        <div style={{ 
          fontSize: '64px', 
          marginBottom: '20px',
          filter: 'drop-shadow(0 0 20px rgba(16, 185, 129, 0.5))',
          animation: 'pulse 2s infinite'
        }}>🔓</div>
        <h2 style={{ 
          margin: '0 0 10px 0', 
          fontFamily: '"Orbitron", sans-serif',
          letterSpacing: '2px',
          textTransform: 'uppercase'
        }}>Access Granted</h2>
        <p style={{ color: '#cbd5e1', maxWidth: '80%' }}>Root privileges have been escalated. The system is now under your control.</p>
        <div style={{ 
          marginTop: '30px', 
          padding: '10px 20px', 
          border: '1px solid #10b981', 
          borderRadius: '4px',
          background: 'rgba(16, 185, 129, 0.1)',
          fontFamily: 'monospace'
        }}>
          user: root<br/>
          group: sudo
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      padding: '20px 30px',
      background: 'linear-gradient(180deg, rgba(239, 68, 68, 0.05) 0%, rgba(0,0,0,0) 100%)',
      position: 'relative',
      overflow: 'auto',
      boxSizing: 'border-box'
    }}>
      {/* Background Grid Effect */}
      <div style={{
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px)',
        backgroundSize: '20px 20px',
        pointerEvents: 'none'
      }} />

      {/* Main Content Area */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        maxWidth: '420px',
        zIndex: 10,
        animation: isShaking ? 'shake 0.5s cubic-bezier(.36,.07,.19,.97) both' : 'none'
      }}>
        <div style={{ 
          fontSize: '48px', 
          marginBottom: '12px',
          filter: 'drop-shadow(0 0 15px rgba(239, 68, 68, 0.4))'
        }}>🔒</div>
        
        <h2 style={{ 
          color: '#f1f5f9', 
          margin: '0 0 6px 0',
          fontFamily: '"Orbitron", sans-serif',
          letterSpacing: '3px',
          textTransform: 'uppercase',
          textShadow: '0 0 10px rgba(239, 68, 68, 0.5)',
          fontSize: '18px',
          textAlign: 'center',
          animation: 'glitch 3s infinite'
        }}>System Locked</h2>
        
        <p style={{ 
          color: '#94a3b8', 
          marginBottom: '20px', 
          textAlign: 'center', 
          fontSize: '12px',
          lineHeight: '1.5'
        }}>
          Root privileges required. Enter the 3-part password to proceed.
        </p>
        
        {accessState !== 'idle' ? (
          <div style={{ width: '100%', textAlign: 'center' }}>
            <div style={{ 
              marginBottom: '10px', 
              color: accessState === 'verifying' ? '#3b82f6' : '#10b981', 
              fontFamily: 'monospace', 
              fontSize: '14px',
              fontWeight: 'bold',
              letterSpacing: '1px'
            }}>
              {accessState === 'verifying' ? 'VERIFYING HASH...' : 'ESCALATING PRIVILEGES...'}
            </div>
            <div style={{ 
              width: '100%', 
              height: '6px', 
              background: 'rgba(255,255,255,0.1)', 
              borderRadius: '3px',
              overflow: 'hidden',
              position: 'relative'
            }}>
              {/* Indeterminate progress bar */}
              <div style={{ 
                height: '100%', 
                background: accessState === 'verifying' ? '#3b82f6' : '#10b981', 
                width: '30%',
                position: 'absolute',
                left: '-30%',
                animation: 'indeterminate 1s infinite linear'
              }} />
            </div>
            <style>{`
              @keyframes indeterminate {
                0% { left: -30%; }
                100% { left: 100%; }
              }
            `}</style>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <div style={{ marginBottom: '15px', position: 'relative' }}>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter Root Password"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'rgba(15, 23, 42, 0.6)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  color: '#fff',
                  outline: 'none',
                  fontSize: '14px',
                  letterSpacing: '1px',
                  transition: 'all 0.3s ease',
                  boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#ef4444';
                  e.target.style.boxShadow = '0 0 0 2px rgba(239, 68, 68, 0.2), inset 0 2px 4px rgba(0,0,0,0.3)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.boxShadow = 'inset 0 2px 4px rgba(0,0,0,0.3)';
                }}
                autoFocus
              />
            </div>
            
            {error && (
              <div style={{ 
                color: '#ef4444', 
                fontSize: '11px', 
                marginBottom: '12px',
                textAlign: 'center',
                background: 'rgba(239, 68, 68, 0.1)',
                padding: '6px 8px',
                borderRadius: '6px',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                fontWeight: '600',
                letterSpacing: '0.5px'
              }}>
                ⚠️ {error}
              </div>
            )}

            <button
              type="submit"
              style={{
                width: '100%',
                padding: '12px',
                background: isHovered ? 'linear-gradient(135deg, #dc2626, #991b1b)' : 'linear-gradient(135deg, #ef4444, #b91c1c)',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontWeight: '600',
                fontSize: '13px',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: isHovered ? '0 4px 12px rgba(239, 68, 68, 0.4)' : '0 2px 4px rgba(0,0,0,0.2)',
                transform: isHovered ? 'translateY(-1px)' : 'translateY(0)'
              }}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              Authenticate
            </button>
          </form>
        )}
      </div>

      {/* Hint Section - Pushed to bottom */}
      <div style={{ 
        marginTop: 'auto', 
        padding: '10px 12px', 
        background: 'rgba(59, 130, 246, 0.1)', 
        borderRadius: '8px', 
        border: '1px dashed rgba(59, 130, 246, 0.3)',
        width: '100%',
        maxWidth: '420px',
        boxSizing: 'border-box',
        zIndex: 10,
        fontFamily: 'monospace'
      }}>
        <div style={{ 
          color: '#60a5fa', 
          fontSize: '11px', 
          fontWeight: '600', 
          marginBottom: '6px',
          textTransform: 'uppercase',
          letterSpacing: '1px'
        }}>
          💡 MISSION BRIEF
        </div>
        <div style={{ color: '#cbd5e1', fontSize: '12px', lineHeight: '1.6' }}>
          <div style={{ marginBottom: '6px' }}>The password has been split into 3 parts.</div>
          <div>
            <span style={{ color: '#60a5fa', fontWeight: '600' }}>Hint 1:</span> The journey begins where every hacker starts... check the <strong style={{ color: '#fff' }}>Terminal</strong> for hidden files.
          </div>
        </div>
      </div>
    </div>
  );
};

export default RootAccess;
